import { UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import PaginatedSelect from "@/components/ui/PaginatedSelect";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptLabelId } from "@/queries";
import { usePromptLabels } from "@/queries/prompt-labels";

export interface ReleaseLabel {
  id?: number;
  name: string;
}

const ReleaseLabelSelector = ({
  selectedLabel,
  onLabelSelect,
  onLabelRemove,
  showLabel = true,
  promptId: overrideId,
}: {
  selectedLabel: ReleaseLabel | undefined;
  onLabelSelect: (label: ReleaseLabel) => void;
  onLabelRemove: (id: number) => void;
  showLabel?: boolean;
  promptId?: number;
}) => {
  const userToken = useUserToken();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const { promptId: urlId } = useParams();
  const promptId = overrideId || urlId;
  const labelIdQuery = usePromptLabelId(Number(promptId as unknown), selectedLabel?.name ?? "");
  const labelId = labelIdQuery.data;
  const isLoadingLabelId = labelIdQuery.isLoading;

  const defaultOption = {
    id: -1,
    name: "Latest (default)",
  };

  const Query = (
    page: number,
  ): UseQueryResult<{ labels: ReleaseLabel[]; total: number; pages: number }, unknown> | null => {
    const result = usePromptLabels({
      userToken,
      workspace_id: workspaceId,
      prompt_id: promptId ? Number(promptId) : undefined,
      page,
    });

    if (!result.data) {
      return result as unknown as UseQueryResult<
        { labels: ReleaseLabel[]; total: number; pages: number },
        unknown
      > | null;
    }

    return {
      ...result,
      data: {
        labels: result.data.prompt_labels.map(label => ({
          id: label.id,
          name: label.name,
        })),
        total: result.data.total,
        pages: result.data.pages,
      },
    } as UseQueryResult<{ labels: ReleaseLabel[]; total: number; pages: number }, unknown> | null;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLabelChange = (value: any) => {
    if (value.item.id === -1) {
      onLabelRemove?.(selectedLabel?.id ?? 0);
      return;
    }
    onLabelSelect(value.item);
  };

  if (isLoadingLabelId && selectedLabel) {
    return (
      <div>
        {showLabel && (
          <label htmlFor="release-label" className="mb-1 block text-sm font-medium">
            Release Label
          </label>
        )}
        <Skeleton width="100%" height="2.5rem" />
      </div>
    );
  }

  return (
    <div>
      {showLabel && (
        <label htmlFor="release-label" className="mb-1 block text-sm font-medium">
          Release Label
        </label>
      )}

      <PaginatedSelect<ReleaseLabel, { labels: ReleaseLabel[]; total: number; pages: number }>
        query={Query}
        renderItem={label => label.name}
        getItemValue={label => label.id?.toString() || ""}
        extractData={data => ({
          data: [defaultOption, ...data.labels],
          totalPages: data.pages,
        })}
        placeholder="Latest (default)"
        value={
          selectedLabel && labelId
            ? {
                id: labelId.toString(),
                item: {
                  id: labelId,
                  name: selectedLabel.name,
                },
              }
            : {
                id: defaultOption.id.toString(),
                item: defaultOption,
              }
        }
        onValueChange={handleLabelChange}
      />
    </div>
  );
};

export default ReleaseLabelSelector;
